import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Ticket, Plus, Download, RefreshCw, Search, Trash2, FileText,
  CheckCircle2, XCircle, AlertTriangle, Clock, Copy, Award, Video,
  ExternalLink, Link as LinkIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'dealfuel', label: 'DealFuel', prefix: 'DEALF' },
  { id: 'dealify', label: 'Dealify', prefix: 'DLFY' },
  { id: 'dealmirror', label: 'DealMirror', prefix: 'DMRR' },
  { id: 'pitchground', label: 'PitchGround', prefix: 'PGRD' },
  { id: 'appsumo', label: 'AppSumo', prefix: 'ASUMO' },
  { id: 'direct', label: 'Direct sale', prefix: 'DIRC' },
  { id: 'other', label: 'Other', prefix: 'OTHR' },
];

const LTD_PACKAGES = [
  { id: 'ltd_founders_1', label: 'Founders Solo (1 site, 500 credits)' },
  { id: 'ltd_founders_3', label: 'Founders Studio (3 sites, 1500 credits)' },
  { id: 'ltd_founders_10', label: 'Founders Agency (10 sites, 4000 credits)' },
  { id: 'ltd_founders_25', label: 'Founders Scale (25 sites, 8000 credits)' },
];

const STATUS_BADGE = {
  unused:    { color: 'bg-blue-500/20 text-blue-300 border-blue-500/30',     label: 'Unused' },
  redeemed:  { color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', label: 'Redeemed' },
  refunded:  { color: 'bg-amber-500/20 text-amber-300 border-amber-500/30',  label: 'Refunded' },
  void:      { color: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',     label: 'Void' },
  redeeming: { color: 'bg-purple-500/20 text-purple-300 border-purple-500/30', label: 'Redeeming…' },
};

function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || { color: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30', label: status };
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-medium border rounded ${b.color}`}>{b.label}</span>;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

export default function RedemptionAdminTab({ token }) {
  const [view, setView] = useState('list');
  const [codes, setCodes] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, page: 1, per_page: 50 });
  const [filters, setFilters] = useState({ status: '', platform: '', batch_id: '', q: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.platform) params.platform = filters.platform;
      if (filters.batch_id) params.batch_id = filters.batch_id;
      if (filters.q) params.q = filters.q;
      const res = await axios.get(`${API_URL}/api/admin/redemption/list`, { headers, params });
      setCodes(res.data.items || []);
      setPagination({
        total: res.data.total,
        pages: res.data.pages,
        page: res.data.page,
        per_page: res.data.per_page,
      });
    } catch (e) {
      console.error(e);
      alert('Failed to load codes: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [token, page, filters]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/redemption/batches`, { headers });
      setBatches(res.data || []);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const voidCode = async (code) => {
    if (!window.confirm(`Void code ${code}? This cannot be undone (but the code remains in the DB for audit).`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/redemption/void`, { code }, { headers });
      fetchList();
    } catch (e) {
      alert('Failed to void: ' + (e.response?.data?.detail || e.message));
    }
  };

  const voidBatch = async (batch_id) => {
    if (!window.confirm(`Void ALL unused codes in batch "${batch_id}"? Already-redeemed codes are unaffected.`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/redemption/void-batch`, { batch_id }, { headers });
      alert(`Voided ${res.data.voided_count} codes.`);
      fetchList(); fetchBatches();
    } catch (e) {
      alert('Failed to void batch: ' + (e.response?.data?.detail || e.message));
    }
  };

  const exportBatch = async (batch_id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/redemption/export/${encodeURIComponent(batch_id)}`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `redemption-export-${batch_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => {/* silent */});
  };

  return (
    <div className="space-y-6" data-testid="redemption-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Ticket className="w-6 h-6 text-[#00E5FF]" />
          <h2 className="text-2xl font-bold text-white">Redemption Codes</h2>
        </div>
        <div className="flex bg-[#1a1a1a] border border-white/10 rounded-lg p-1">
          {[
            { id: 'list', label: 'All Codes' },
            { id: 'generate', label: 'Generate Batch' },
            { id: 'stats', label: 'Stats' },
            { id: 'refunds', label: 'Refund Log' },
            { id: 'founders', label: 'Founders Circle' },
          ].map(v => (
            <button key={v.id}
              onClick={() => setView(v.id)}
              className={`px-3 py-1.5 text-sm rounded ${view === v.id ? 'bg-[#0055FF] text-white' : 'text-[#A1A1AA] hover:text-white'}`}
            >{v.label}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 bg-[#0a0a0a] border border-[#0055FF]/30 rounded-lg px-4 py-3">
        <LinkIcon className="w-4 h-4 text-[#00E5FF] shrink-0" />
        <a href="https://aeo-orchestra.com/redeem" target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 group">
          <div className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-medium">Public redeem URL</div>
          <code className="text-sm text-white font-mono break-all group-hover:text-[#00E5FF] transition">https://aeo-orchestra.com/redeem</code>
        </a>
        <button
          onClick={() => { navigator.clipboard.writeText('https://aeo-orchestra.com/redeem'); copy && copy('https://aeo-orchestra.com/redeem'); }}
          className="p-2 text-[#A1A1AA] hover:text-white hover:bg-white/10 rounded transition shrink-0"
          title="Copy URL"
        >
          <Copy className="w-4 h-4" />
        </button>
        <a
          href="https://aeo-orchestra.com/redeem"
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-[#A1A1AA] hover:text-white hover:bg-white/10 rounded transition shrink-0"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {view === 'list' && (
        <ListView
          codes={codes} pagination={pagination} page={page} setPage={setPage}
          filters={filters} setFilters={setFilters}
          loading={loading} onRefresh={fetchList}
          onVoidCode={voidCode} onCopy={copy}
        />
      )}
      {view === 'generate' && (
        <GenerateView
          token={token}
          onGenerated={() => { setView('list'); setFilters({ ...filters }); fetchList(); fetchBatches(); }}
        />
      )}
      {view === 'stats' && (
        <StatsView
          token={token} batches={batches} onRefreshBatches={fetchBatches}
          onVoidBatch={voidBatch} onExportBatch={exportBatch}
        />
      )}
      {view === 'refunds' && <RefundLogView token={token} />}
      {view === 'founders' && <FoundersCircleView token={token} />}
    </div>
  );
}

// ---------------- List View ----------------
function ListView({ codes, pagination, page, setPage, filters, setFilters, loading, onRefresh, onVoidCode, onCopy }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
          <input
            value={filters.q}
            onChange={e => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search code, batch, email…"
            className="w-full pl-9 pr-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#0055FF]"
          />
        </div>
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-white text-sm">
          <option value="">All statuses</option>
          <option value="unused">Unused</option>
          <option value="redeemed">Redeemed</option>
          <option value="refunded">Refunded</option>
          <option value="void">Void</option>
        </select>
        <select value={filters.platform} onChange={e => setFilters({ ...filters, platform: e.target.value })}
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-white text-sm">
          <option value="">All platforms</option>
          {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <input
          value={filters.batch_id}
          onChange={e => setFilters({ ...filters, batch_id: e.target.value })}
          placeholder="batch_id"
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-white text-sm"
        />
      </div>

      <div className="flex justify-between items-center text-xs text-[#A1A1AA]">
        <div>{pagination.total} codes — page {pagination.page} / {pagination.pages || 1}</div>
        <button onClick={onRefresh} className="flex items-center gap-1 px-2 py-1 hover:text-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-[#A1A1AA] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Tier</th>
                <th className="text-left px-4 py-2">Platform</th>
                <th className="text-left px-4 py-2">Batch</th>
                <th className="text-left px-4 py-2">Redeemed by</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#A1A1AA]">{loading ? 'Loading…' : 'No codes match the current filter.'}</td></tr>
              )}
              {codes.map(c => (
                <tr key={c.code} className="border-t border-white/5">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-white">{c.code}</span>
                      <button onClick={() => onCopy(c.code)} className="text-[#A1A1AA] hover:text-white" title="Copy code">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-2 text-[#A1A1AA] text-xs">{c.package_id}</td>
                  <td className="px-4 py-2 text-[#A1A1AA] text-xs">{c.platform}</td>
                  <td className="px-4 py-2 text-[#A1A1AA] text-xs">{c.batch_id}</td>
                  <td className="px-4 py-2 text-[#A1A1AA] text-xs">{c.redeemed_by_email || '—'}</td>
                  <td className="px-4 py-2 text-[#A1A1AA] text-xs">{formatDateTime(c.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    {c.status === 'unused' && (
                      <button onClick={() => onVoidCode(c.code)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 ml-auto">
                        <Trash2 className="w-3.5 h-3.5" /> Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="px-3 py-1 bg-[#1a1a1a] border border-white/10 rounded text-white disabled:opacity-30">Prev</button>
          <span className="px-3 py-1 text-[#A1A1AA]">Page {pagination.page} / {pagination.pages}</span>
          <button disabled={page >= pagination.pages} onClick={() => setPage(page + 1)}
            className="px-3 py-1 bg-[#1a1a1a] border border-white/10 rounded text-white disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}

// ---------------- Generate View ----------------
function GenerateView({ token, onGenerated }) {
  const [form, setForm] = useState({
    platform: 'dealfuel',
    package_id: 'ltd_founders_3',
    quantity: 100,
    batch_id: '',
    override_max_domains: '',
    override_daily_credit_cap: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.batch_id.trim()) { alert('batch_id is required'); return; }
    if (form.quantity < 1 || form.quantity > 2000) { alert('Quantity must be 1-2000'); return; }
    if (!window.confirm(`Generate ${form.quantity} codes for ${form.platform} / ${form.package_id} (batch "${form.batch_id}")?`)) return;

    setSubmitting(true);
    try {
      const payload = {
        platform: form.platform,
        package_id: form.package_id,
        quantity: parseInt(form.quantity, 10),
        batch_id: form.batch_id.trim(),
        notes: form.notes || null,
      };
      if (form.override_max_domains) payload.override_max_domains = parseInt(form.override_max_domains, 10);
      if (form.override_daily_credit_cap) payload.override_daily_credit_cap = parseInt(form.override_daily_credit_cap, 10);

      const res = await axios.post(`${API_URL}/api/admin/redemption/generate-batch`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `redemption-${form.batch_id}-${form.platform}-${form.package_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      alert(`Generated ${form.quantity} codes. CSV downloaded.`);
      onGenerated?.();
    } catch (e) {
      let msg = e.response?.data?.detail || e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch {/* ignore */}
      }
      alert('Generation failed: ' + msg);
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#1a1a1a] border border-white/10 rounded-lg p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-2 mb-2">
        <Plus className="w-5 h-5 text-[#00E5FF]" />
        <h3 className="text-lg font-semibold text-white">Generate Redemption Batch</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Platform</label>
          <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm">
            {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label} ({p.prefix})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Package</label>
          <select value={form.package_id} onChange={e => setForm({ ...form, package_id: e.target.value })}
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm">
            {LTD_PACKAGES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Quantity (1-2000)</label>
          <input type="number" min={1} max={2000} value={form.quantity}
            onChange={e => setForm({ ...form, quantity: e.target.value })}
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm" />
        </div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Batch ID (groups codes for stats/export)</label>
          <input type="text" value={form.batch_id}
            onChange={e => setForm({ ...form, batch_id: e.target.value })}
            placeholder="e.g. dealfuel-launch-2026-q2"
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm" />
        </div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Override max_domains (optional)</label>
          <input type="number" min={1} max={200} value={form.override_max_domains}
            onChange={e => setForm({ ...form, override_max_domains: e.target.value })}
            placeholder="leave blank = use package default"
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm" />
        </div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Override daily_credit_cap (optional)</label>
          <input type="number" min={1} max={10000} value={form.override_daily_credit_cap}
            onChange={e => setForm({ ...form, override_daily_credit_cap: e.target.value })}
            placeholder="leave blank = use package default"
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm" />
        </div>
      </div>

      <div>
        <label className="block text-xs text-[#A1A1AA] mb-1">Notes (optional, max 500 chars)</label>
        <textarea rows={2} value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-white text-sm" />
      </div>

      <button type="submit" disabled={submitting}
        className="w-full md:w-auto px-6 py-2 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded font-medium flex items-center justify-center gap-2">
        {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {submitting ? 'Generating…' : `Generate ${form.quantity} codes & download CSV`}
      </button>
      <p className="text-xs text-[#A1A1AA]">
        Codes are inserted as <code>status=unused</code>. CSV columns: <code>code, tier, redemption_url</code>.
      </p>
    </form>
  );
}

// ---------------- Stats View ----------------
function StatsView({ token, batches, onRefreshBatches, onVoidBatch, onExportBatch }) {
  const [selected, setSelected] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!selected) { setStats(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/redemption/stats/${encodeURIComponent(selected)}`, { headers });
        setStats(res.data);
      } catch (e) {
        alert('Failed to load stats: ' + (e.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
  }, [selected, token]);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-xs text-[#A1A1AA] mb-1">Select batch</label>
          <select value={selected} onChange={e => setSelected(e.target.value)}
            className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-white text-sm">
            <option value="">— pick a batch —</option>
            {batches.map(b => (
              <option key={b.batch_id} value={b.batch_id}>
                {b.batch_id} — {b.platform} / {b.package_id} ({b.total} codes, {b.redeemed_count} redeemed)
              </option>
            ))}
          </select>
        </div>
        <button onClick={onRefreshBatches} className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-sm text-white hover:bg-black/40 flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh batches
        </button>
        {selected && (
          <>
            <button onClick={() => onExportBatch(selected)}
              className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-sm text-white hover:bg-black/40 flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={() => onVoidBatch(selected)}
              className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded text-sm text-red-300 hover:bg-red-500/30 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Void unused
            </button>
          </>
        )}
      </div>

      {loading && <p className="text-[#A1A1AA] text-sm">Loading stats…</p>}
      {stats && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Ticket}      title="Total codes" value={stats.total} accent="#0055FF" />
          <StatCard icon={CheckCircle2} title="Redeemed"   value={stats.redeemed} accent="#10B981" />
          <StatCard icon={Clock}       title="Unused"     value={stats.unused} accent="#3B82F6" />
          <StatCard icon={AlertTriangle} title="Refunded" value={stats.refunded} accent="#F59E0B" />
          <StatCard icon={XCircle}      title="Void"       value={stats.void} accent="#71717A" />
          <StatCard icon={CheckCircle2} title="Conversion rate"
            value={(stats.conversion_rate * 100).toFixed(1) + '%'} accent="#10B981" />
          <StatCard icon={AlertTriangle} title="Refund rate"
            value={stats.redeemed > 0 ? ((stats.refund_rate * 100).toFixed(1) + '%') : '—'} accent="#F59E0B" />
          <StatCard icon={Clock} title="Avg time-to-redeem"
            value={formatDuration(stats.avg_time_to_redeem_seconds)} accent="#6366F1" />
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, title, value, accent }) {
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs uppercase tracking-wide text-[#A1A1AA]">{title}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

// ---------------- Refund Log View (placeholder for Phase 4) ----------------
function RefundLogView({ token }) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/redemption/list`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'refunded', per_page: 100 },
        });
        setRefunds(res.data.items || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [token]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#A1A1AA]">
        Refund webhook integration arrives in Phase 4. For now, this view shows codes with <code>status=refunded</code>.
      </p>
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/30 text-[#A1A1AA] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">Platform</th>
              <th className="text-left px-4 py-2">Refund reason</th>
              <th className="text-left px-4 py-2">Refunded at</th>
            </tr>
          </thead>
          <tbody>
            {refunds.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#A1A1AA]">{loading ? 'Loading…' : 'No refunded codes yet.'}</td></tr>
            )}
            {refunds.map(c => (
              <tr key={c.code} className="border-t border-white/5">
                <td className="px-4 py-2 font-mono text-xs text-white">{c.code}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{c.redeemed_by_email || '—'}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{c.platform}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{c.refund_metadata?.reason || '—'}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{formatDateTime(c.refund_metadata?.refunded_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------- Founders Circle View (Phase 6) ----------------
function FoundersCircleView({ token }) {
  const [founders, setFounders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchFounders = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/redemption/founders`, { headers });
      setFounders(res.data.founders || []);
    } catch (e) {
      alert('Failed to load founders: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { fetchFounders(); }, [fetchFounders]);

  const recordedCount = founders.filter(f => f.founder_loom_recorded).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-[#A1A1AA]">
          {founders.length} founder{founders.length === 1 ? '' : 's'} ·
          <span className="ml-1 text-emerald-400">{recordedCount} with Loom recorded</span>
        </div>
        <button onClick={fetchFounders} className="flex items-center gap-1 px-2 py-1 text-xs text-[#A1A1AA] hover:text-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/30 text-[#A1A1AA] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Founder #</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Platform</th>
              <th className="text-left px-4 py-2">Joined</th>
              <th className="text-left px-4 py-2">Loom</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {founders.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#A1A1AA]">{loading ? 'Loading...' : 'No founders yet. The first surprise gift delivery will populate this list.'}</td></tr>
            )}
            {founders.map(f => (
              <tr key={f.id} className="border-t border-white/5">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5 text-purple-300" />
                    <span className="font-bold text-white">#{f.founder_number}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-xs text-white">{f.email}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{f.surprise_platform || '-'}</td>
                <td className="px-4 py-2 text-xs text-[#A1A1AA]">{formatDateTime(f.surprise_delivered_at)}</td>
                <td className="px-4 py-2">
                  {f.founder_loom_recorded && f.founder_loom_url ? (
                    <a href={f.founder_loom_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Recorded
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                      <Clock className="w-3.5 h-3.5" /> Pending
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditing(f)}
                    className="text-xs text-[#A1A1AA] hover:text-white inline-flex items-center gap-1 ml-auto">
                    <Video className="w-3.5 h-3.5" /> {f.founder_loom_recorded ? 'Update' : 'Mark recorded'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <LoomEditModal
          founder={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchFounders(); }}
        />
      )}
    </div>
  );
}

function LoomEditModal({ founder, token, onClose, onSaved }) {
  const [url, setUrl] = React.useState(founder.founder_loom_url || '');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async () => {
    if (!url.trim() || url.trim().length < 10) { setError('Please enter a valid Loom URL'); return; }
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API_URL}/api/admin/redemption/founders/${founder.id}/loom`,
        { loom_url: url.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-purple-300" />
          <h3 className="text-lg font-bold text-white">Loom for Founder #{founder.founder_number}</h3>
        </div>
        <div className="text-sm text-[#A1A1AA]">{founder.email}</div>
        <div>
          <label className="block text-xs text-[#A1A1AA] mb-1">Loom URL</label>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://www.loom.com/share/..."
            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm"
          />
        </div>
        {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">{error}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 px-4 py-2 bg-[#0055FF] hover:bg-[#3377FF] text-white rounded text-sm font-medium flex items-center justify-center gap-1.5">
            {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

